import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { MessageSquareWarningIcon, BanknoteIcon, HelpCircleIcon, SplitIcon } from 'lucide-react';
const painPoints = [{
  icon: BanknoteIcon,
  title: 'Chasing people is awkward',
  description: 'You paid. They didn\'t. Now you\'re the one sending "hey, any chance you can…" for the third time.'
}, {
  icon: MessageSquareWarningIcon,
  title: 'Group chats become messy',
  description: 'Receipt screenshots. "Who paid for what?" messages. Three different payment apps. Pure chaos.'
}, {
  icon: HelpCircleIcon,
  title: 'Someone always forgets',
  description: 'There\'s always one person who "meant to pay you back." Weeks go by. You just absorb it.'
}, {
  icon: SplitIcon,
  title: 'It ruins the vibe',
  description: "A great night out shouldn't end with a spreadsheet. Money shouldn't make things weird."
}];
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
export function ProblemSection() {
  return <section className="relative py-20 lg:py-28 bg-gray-950 text-white overflow-hidden" data-id="element-206">
      {/* Subtle texture */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(251,191,36,0.06),transparent_50%)]" data-id="element-207" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-208">
        {/* Section header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-2xl mx-auto mb-16" data-id="element-209">
          <h2 className="text-3xl lg:text-5xl font-extrabold leading-tight tracking-tight mb-4" data-id="element-210">
            Ever had to chase friends{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-kitty-yellow-400 to-orange-400" data-id="element-211">
              for money?
            </span>
          </h2>
          <p className="text-lg text-gray-400 max-w-md mx-auto" data-id="element-212">
            No more "you owe me £20" messages. We fixed it.
          </p>
        </motion.div>

        {/* Pain points grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-60px'
      }} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-213">
          {painPoints.map(point => <motion.div key={point.title} variants={itemVariants} className="relative rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6 lg:p-7" data-id="element-214">
              <div className="w-10 h-10 rounded-xl bg-kitty-yellow-500/10 flex items-center justify-center mb-5" data-id="element-215">
                <point.icon className="w-5 h-5 text-kitty-yellow-400" data-id="element-216" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2 leading-snug" data-id="element-217">
                {point.title}
              </h3>
              <p className="text-sm text-gray-400 leading-relaxed" data-id="element-218">
                {point.description}
              </p>
            </motion.div>)}
        </motion.div>

        {/* Bottom nudge */}
        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.5,
        duration: 0.6
      }} className="text-center text-gray-500 text-sm mt-12" data-id="element-219">
          There has to be a better way.{' '}
          <span className="text-kitty-yellow-400 font-medium" data-id="element-220">There is.</span>
        </motion.p>
      </div>
    </section>;
}